/**
 * 설정 모드 사이드바 — me.permissions 설정 그룹 자식의 displayOrder·urlPath로 구성.
 * menuKey 난수와 무관하게 urlPath로 라우트·권한 플래그를 결정한다.
 */
import type { AdminMenuTreeNode } from '@/entities/menu/types';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { UserRound } from 'lucide-react';
import { filterSettingsSidebarByRole } from './filterSettingsSidebarByRole';
import {
  isSettingsSidebarUrl,
  resolveRouteByUrlPath,
  SETTINGS_SIDEBAR_URLS,
} from './menuUrlRouteMap';
import {
  settingsSidebarMenuItems,
  type MenuLink,
  type SidebarMenuItem,
} from './sidebarMenuConfig';

/** 설정 하위 urlPath → 역할 게이트 */
const SETTINGS_URL_FLAGS: Record<
  string,
  Pick<MenuLink, 'fullAccessOnly' | 'masterOnly'>
> = {
  [ROUTE_PATHS.SETTINGS_MENUS]: { masterOnly: true },
  [ROUTE_PATHS.SETTINGS_ACCOUNTS]: { fullAccessOnly: true },
  [ROUTE_PATHS.SETTINGS_AUDIT_LOG]: { fullAccessOnly: true },
  [ROUTE_PATHS.SETTINGS_PARTNERS]: { fullAccessOnly: true },
};

const MY_ACCOUNT_LINK: MenuLink = {
  key: ROUTE_PATHS.MY_ACCOUNT,
  icon: UserRound,
  label: '내 프로필',
};

const byDisplayOrder = (a: AdminMenuTreeNode, b: AdminMenuTreeNode) =>
  a.displayOrder - b.displayOrder;

const isSettingsGroupNode = (node: AdminMenuTreeNode): boolean => {
  if (isSettingsSidebarUrl(node.urlPath)) return true;
  const kids = (node.children ?? []).filter((c) => c.visible);
  if (kids.length === 0) return false;
  return kids.some((c) => isSettingsSidebarUrl(c.urlPath));
};

/**
 * 설정 그룹 자식을 displayOrder 순·urlPath 기준으로 MenuLink로 변환한다.
 */
export const mapSettingsChildrenToLinks = (
  settingsNode: AdminMenuTreeNode | undefined,
): MenuLink[] => {
  if (!settingsNode?.children?.length) return [];

  return [...settingsNode.children]
    .filter((child) => child.visible)
    .sort(byDisplayOrder)
    .flatMap((child) => {
      const route = resolveRouteByUrlPath(child.urlPath);
      if (!route || !SETTINGS_SIDEBAR_URLS.has(route.path)) return [];
      const flags = SETTINGS_URL_FLAGS[route.path] ?? {};
      return [
        {
          key: route.path,
          label: child.menuName || route.label,
          icon: route.icon,
          menuCode: child.menuKey,
          ...flags,
        },
      ];
    });
};

/**
 * 설정 사이드바 구성.
 * - 서버 설정 그룹 자식 순서(urlPath) 우선
 * - 「내 프로필」이 없으면 맨 앞 고정
 * - 트리 없으면 하드코딩 폴백
 */
export const buildSettingsSidebarFromMenuTree = (
  menus: AdminMenuTreeNode[] | undefined | null,
  role?: string | null,
  roles?: string[] | null,
): SidebarMenuItem[] => {
  const settingsNode = menus?.find((node) => isSettingsGroupNode(node));
  const fromTree = mapSettingsChildrenToLinks(settingsNode);

  const base: MenuLink[] =
    fromTree.length > 0
      ? fromTree.some((link) => link.key === ROUTE_PATHS.MY_ACCOUNT)
        ? fromTree
        : [MY_ACCOUNT_LINK, ...fromTree]
      : (settingsSidebarMenuItems.filter(
          (item): item is MenuLink => 'key' in item && !('children' in item),
        ) as MenuLink[]);

  return filterSettingsSidebarByRole(base, role, roles);
};
